import { useMeeting, usePubSub } from "@videosdk.live/react-sdk";
import React, { useState, useEffect, useMemo } from "react";
import ParticipantView from "../components/ParticipantView";
import CharacterView from "../components/CharacterView";
import { logType, useJourney } from "../utils/common";
import { useAppContext } from "../AppContextDef";
import MainCard from "../components/MainCard";
import { useTranslation } from "react-i18next";
import useIsMobile from "../utils/useIsMobile";
import useIsTab from "../utils/useIsTab";
import imageSrc from "../repeat_audio_icon.png";
import DisagreeDialog from "../components/DisagreeDialog";
import usePubsubFunction from "../utils/usePubsubFunction";

const PersonalDetails = ({
  handleAgree,
  handleDisagree,
  currentPage,
  setCharacterJoined,
  characterJoined,
  capturedImage,
  setCapturedImage,
  getScreenshotImage,
  handleDisagreeAPI,
  abortSignal,
}) => {
  const {
    journeyConfig,
    applicationNo,
    allDetails,
    journeyData,
    selectedLanguageCode,
    HeaderSectionHeight,
  } = useAppContext();
  const { t, i18n } = useTranslation();
  let isMobile = useIsMobile();
  let isTab = useIsTab();
  const [buttonSectionHeightRef, setButtonSectionHeightRef] = useState(null);
  const { publish: agreePublish } = usePubSub(`AGREE`);
  const { publish: disagreePublish } = usePubSub(`DISAGREE`);
  const { publish: mediaErrorPublish } = usePubSub(`MEDIA_ERROR`);
  const { publish: journeyConfigPublish } = usePubSub(`JOURNEY_CONFIG_DATA`);
  const { publish: journeyDataPublish } = usePubSub(`JOURNEY_DATA`);
  const { publish: langPublish } = usePubSub("SELECT_LANG");
  const pubsubPublishMethod = usePubsubFunction()

  const { logJourney } = useJourney();
  const ButtonSectionHeight = useMemo(
    () => buttonSectionHeightRef?.offsetHeight,
    [buttonSectionHeightRef]
  );

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLanguageCode);
  }, [selectedLanguageCode]);

  const [mediaError, setMediaError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [isCharacterSpeaking, setIsCharacterSpeaking] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [showDisagreeDialog, setShowDisagreeDialog] = useState(false);
  const [inputValue, setInputValue] = useState({
    proposerName:
      `${allDetails?.iciciApplicationDetails?.proposerFstName}${" " + allDetails?.iciciApplicationDetails?.proposerLstName
      }` || "NA",
    lifeInsuranceName:
      `${allDetails?.iciciApplicationDetails?.lifeAssuredFstName}${" " + allDetails?.iciciApplicationDetails?.lifeAssuredLstName
      }` || "NA",
    mobileNumber: allDetails?.iciciApplicationDetails?.mobNo || "NA",
    dob: allDetails?.iciciApplicationDetails?.proposerDOB || "NA",
    email: allDetails?.iciciApplicationDetails?.emailId || "NA",
    education: allDetails?.iciciApplicationDetails?.proposerEducation || "NA",
    occupation: allDetails?.iciciApplicationDetails?.proposerOccupation || "NA",
    income_p_a: allDetails?.iciciApplicationDetails?.proposerIncome || "NA",
    gender: allDetails?.iciciApplicationDetails?.gender || "NA",
  });

  const { end } = useMeeting({
    onCharacterJoined: () => {
      setCharacterJoined(true);
    },
  });

  useEffect(() => {
    // startJourney();

    pubsubPublishMethod(journeyConfigPublish, "journeyConfig", { persist: true }, journeyConfig);
    pubsubPublishMethod(journeyDataPublish, "journeyData", { persist: true }, journeyData);

    logJourney({
      type: logType.NAVIGATION,
      message: "Navigated to Personal Details Page",
    });
  }, []);

  useEffect(() => {
    const requestMediaPermissions = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        pubsubPublishMethod(mediaErrorPublish, "Error", { persist: true }, { err: null });
        setMediaError(null); // No error if permissions granted
      } catch (err) {
        setMediaError(
          "Unable to access camera or microphone. Please enable them to continue."
        );
        pubsubPublishMethod(mediaErrorPublish,
          "Error",
          { persist: true },
          {
            err: "Unable to access camera or microphone. Please enable them to continue.",
          },
        );
        console.error("Error accessing media devices:", err);
      }
    };

    requestMediaPermissions();
  }, []);

  useEffect(() => {
    if (selectedLanguageCode) {
      pubsubPublishMethod(langPublish, selectedLanguageCode, { persist: true });
    }
  }, [selectedLanguageCode]);

  const isEnabled = agreed && !isCharacterSpeaking && messageSent;

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  let isMobileOrTab = isMobile || isTab;
  let spacingHeight = isMobile
    ? HeaderSectionHeight + ButtonSectionHeight + 180 + 70 + 20
    : isTab
      ? HeaderSectionHeight + ButtonSectionHeight + 290 + 70 + 40
      : ButtonSectionHeight + 160 + 56 + 40;

  let buttonCondition = !isCharacterSpeaking && messageSent;

  return (
    <div className="flex flex-col-reverse  lg:flex-row flex-1 py-3 px-3 lg:py-9 lg:px-8 xl:px-14 gap-3 md:gap-4 lg:gap-5 xl:gap-16">
      <MainCard
        mainTitle={t("Personal Details")}
        FirstSection={
          <div
            style={{
              // height: isMobile && `calc(100vh - ${spacingHeight}px)`,
              height: `${viewportHeight - spacingHeight}px`,
            }}
            className="mt-3 lg:mt-14 px-1 lg:px-0 overflow-y-auto scrollable-content scrollbar scrollbar-primary"
          >
            <div className="flex flex-col flex-1">
              <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                {t("Proposer Name")}
              </p>
              <p className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150">
                {inputValue.proposerName}
              </p>
            </div>
            <div className="flex flex-1 flex-col mt-3 lg:mt-4">
              <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                {t("Life Assured Name")}
              </p>
              <p className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150">
                {inputValue.lifeInsuranceName}
              </p>
            </div>

            <div className="flex mt-3 lg:mt-4">
              <div className="flex flex-col flex-1">
                <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                  {t("Proposer date of birth")}
                </p>
                <p className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150">
                  {inputValue.dob}
                </p>
              </div>
              <div className="flex flex-col flex-1">
                <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                  {t("Life assured gender")}
                </p>
                <p className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150">
                  {inputValue.gender}
                </p>
              </div>
            </div>

            <div className="flex flex-col mt-3 lg:mt-4">
              <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                {t("Registered Email Address")}
              </p>
              <p className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150">
                {inputValue.email}
              </p>
            </div>

            <div className="flex mt-3 lg:mt-4">
              <div className="flex flex-col flex-1">
                <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                  {t("Mobile Number")}
                </p>
                <p className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150">
                  {inputValue.mobileNumber}
                </p>
              </div>
              <div className="flex flex-col flex-1">
                <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                  {t("Education")}
                </p>
                <p className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150">
                  {inputValue.education}
                </p>
              </div>
            </div>

            <div className="flex mt-3 lg:mt-4">
              <div className="flex flex-1 flex-col ">
                <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                  {t("Occupation")}
                </p>
                <p className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150">
                  {inputValue.occupation}
                </p>
              </div>

              <div className="flex flex-1 flex-col ">
                <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                  {t("Income (per annum)")}
                </p>
                <p className="mt-1 text-sm md:text-base lg:text-lg font-semibold text-black-150">
                  {inputValue.income_p_a}₹
                </p>
              </div>
            </div>
          </div>
        }
        SecondSection={
          <div
            ref={setButtonSectionHeightRef}
            className="flex mt-3 lg:mt-10 px-1 lg:px-0 justify-between"
          >
            <div className="flex flex-1 text-sm md:text-base">
              <button
                disabled={!isEnabled}
                className={`disabled:bg-opacity-15 disabled:cursor-not-allowed bg-orange-150 text-white p-2 md:p-3 rounded-md w-full font-bold lg:max-w-max`}
                onClick={() => {
                  handleAgree();
                  pubsubPublishMethod(agreePublish,
                    "Agree",
                    { persist: true },
                    {
                      isAgreeClick: true,
                      currentPage: "PersonalDetails",
                    },
                  );
                  logJourney({
                    type: logType.ACTION,
                    message: "Agree Button Clicked In Personal Details Page",
                  });
                }}
              >
                {t("I Agree")}
              </button>
              <button
                className="bg-gray-100 text-black-150 p-2 md:p-3 rounded-md border border-darkGray-150 ml-3 w-full font-bold lg:max-w-max"
                onClick={async () => {
                  setShowDisagreeDialog(true);
                }}
              >
                {t("Disagree")}
              </button>
            </div>
            <button
              className={`bg-blue-250 text-blue-150 disabled:cursor-not-allowed flex items-center justify-center p-2 md:p-3 rounded-md font-bold ml-3 text-sm md:text-base lg:ml-0 max-w-max`}
              disabled={!buttonCondition}
              onClick={() => {
                setMessageSent(false);
              }}
            >
              <img src={imageSrc} alt="icon" className="w-6 h-6" />
            </button>
          </div>
        }
      />
      <DisagreeDialog
        open={showDisagreeDialog}
        onCancel={() => {
          setShowDisagreeDialog(false);
        }}
        onDisagree={async (disagreeReason) => {
          pubsubPublishMethod(disagreePublish,
            "Disagree",
            { persist: true },
            {
              isDisagreeClick: true,
              currentPage: "PersonalDetails",
            },
          );
          await logJourney({
            type: logType.ERROR,
            message:
              "Disagree Button Clicked In Personal Details Page with reason: " +
              disagreeReason,
          });
          await handleDisagreeAPI(disagreeReason);
          end();
          handleDisagree();
        }}
      />
      <div
        className={`flex  flex-col flex-1 ${isMobileOrTab && "sticky top-30"}`}
      >
        {mediaError ? (
          <div className="text-red-600 font-semibold mb-2">{mediaError}</div>
        ) : isMobile ? (
          <div className="flex ">
            <div className="flex flex-1 rounded-2xl p-2 items-center ">
              <div className="">
                <p className="text-sm lg:text-base font-bold text-black-150 align-middle justify-center">
                  {`${t(`Application number`)}:`}
                </p>
                <p className="text-sm lg:text-base font-bold text-black-150 align-middle justify-center">
                  {`${applicationNo}`}
                </p>
              </div>
            </div>
            <div className="flex flex-1 ">
              <ParticipantView
                currentPage={currentPage}
                capturedImage={capturedImage}
                setCapturedImage={setCapturedImage}
                setAgreed={setAgreed}
                agreed={agreed}
                abortSignal={abortSignal}
              />
            </div>
          </div>
        ) : (
          <ParticipantView
            currentPage={currentPage}
            capturedImage={capturedImage}
            setCapturedImage={setCapturedImage}
            setAgreed={setAgreed}
            agreed={agreed}
            abortSignal={abortSignal}
          />
        )}
        {
          <CharacterView
            characterJoined={characterJoined}
            agreed={agreed}
            messageSent={messageSent}
            setMessageSent={setMessageSent}
            isCharacterSpeaking={isCharacterSpeaking}
            setIsCharacterSpeaking={setIsCharacterSpeaking}
            message={journeyConfig?.voiceoverPersonalDetails}
            getScreenshotImage={getScreenshotImage}
            sendPreloadAudio={true}
          />
        }
      </div>
    </div>
  );
};

export default PersonalDetails;
